import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

const API = (path, opts = {}) =>
  fetch(path, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
    ...opts,
  });

const STAGES = ['identified','contacted','demo_booked','proposal_sent','trial','converted','lost'];
const STAGE_COLOURS = {
  identified:    ['#dbeafe','#1e40af'],
  contacted:     ['#fef3c7','#92400e'],
  demo_booked:   ['#ede9fe','#5b21b6'],
  proposal_sent: ['#fee2e2','#991b1b'],
  trial:         ['#d1fae5','#065f46'],
  converted:     ['#dcfce7','#166534'],
  lost:          ['#f3f4f6','#6b7280'],
};
const ACTIVITY_ICONS = { call:'📞', email:'📧', meeting:'🤝', demo:'🖥️', note:'📝', website_enquiry:'🌐' };
const TIERS = { solo: { base: 9.99, perUser: 0 }, small_firm: { base: 10, perUser: 5 }, firm: { base: 50, perUser: 4 } };

function calcMrr(tier, firmSize) {
  if (!tier || !TIERS[tier]) return null;
  const { base, perUser } = TIERS[tier];
  const size = parseInt(firmSize) || 0;
  return tier === 'solo' ? base : base + size * perUser;
}

function generatePassword() {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

export default function OwnerProspectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [prospect, setProspect] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [form, setForm] = useState({});
  const [lostReason, setLostReason] = useState('');
  const [showLostPrompt, setShowLostPrompt] = useState(false);
  const [actType, setActType] = useState('note');
  const [actBody, setActBody] = useState('');
  const [actSaving, setActSaving] = useState(false);
  const [actError, setActError] = useState('');

  // Convert modal state
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertForm, setConvertForm] = useState({});
  const [convertSaving, setConvertSaving] = useState(false);
  const [convertError, setConvertError] = useState('');
  const [convertedResult, setConvertedResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await API(`/api/prospects/${id}`);
    const d = await res.json();
    if (!res.ok) { setError(d.error || 'Failed'); setLoading(false); return; }
    setProspect(d);
    setActivities(d.activities || []);
    setForm({
      company_name: d.company_name || '',
      contact_name: d.contact_name || '',
      contact_email: d.contact_email || '',
      contact_phone: d.contact_phone || '',
      firm_size: d.firm_size || '',
      source: d.source || '',
      stage: d.stage || 'identified',
      tier_interest: d.tier_interest || '',
      estimated_mrr: d.estimated_mrr || '',
      follow_up_date: d.follow_up_date ? d.follow_up_date.split('T')[0] : '',
      notes: d.notes || '',
    });
    setLostReason(d.lost_reason || '');
    if (d.converted_company_id) {
      setConvertedResult({ company_name: d.converted_company_name, company_id: d.converted_company_id });
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const openConvertModal = () => {
    setConvertError('');
    setConvertForm({
      company_name: form.company_name || '',
      admin_name: form.contact_name || '',
      admin_email: form.contact_email || '',
      admin_phone: form.contact_phone || '',
      subscription_tier: 'flat',
      trial_months: '1',
      admin_password: generatePassword(),
    });
    setShowConvertModal(true);
  };

  const handleConvert = async (e) => {
    e.preventDefault();
    setConvertError('');
    setConvertSaving(true);
    try {
      const onboardRes = await API('/api/owner/onboard', {
        method: 'POST',
        body: JSON.stringify({
          company_name: convertForm.company_name,
          admin_name: convertForm.admin_name,
          admin_email: convertForm.admin_email,
          admin_phone: convertForm.admin_phone || undefined,
          subscription_tier: convertForm.subscription_tier,
          trial_months: parseInt(convertForm.trial_months) || 0,
          admin_password: convertForm.admin_password,
        }),
      });
      const onboardData = await onboardRes.json();
      if (!onboardRes.ok) {
        setConvertError(onboardData.error || 'Failed to create company');
        setConvertSaving(false);
        return;
      }

      // Link the new company back to this prospect
      await API(`/api/prospects/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          converted_company_id: onboardData.company.id,
          stage: 'converted',
        }),
      });

      setConvertedResult({ company_name: onboardData.company.name, company_id: onboardData.company.id });
      setForm(f => ({ ...f, stage: 'converted' }));
      setShowConvertModal(false);
      load();
    } catch (err) {
      setConvertError('Network error — please try again');
    } finally {
      setConvertSaving(false);
    }
  };

  const save = async (overrides = {}) => {
    setSaveError('');
    setSaving(true);
    const body = { ...form, ...overrides };
    if (body.firm_size) body.firm_size = parseInt(body.firm_size);
    if (body.estimated_mrr) body.estimated_mrr = parseFloat(body.estimated_mrr);
    if (!body.estimated_mrr) delete body.estimated_mrr;
    if (!body.follow_up_date) delete body.follow_up_date;
    if (!body.firm_size) delete body.firm_size;
    const res = await API(`/api/prospects/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
    const d = await res.json();
    setSaving(false);
    if (!res.ok) { setSaveError(d.error || 'Failed to save'); return false; }
    setProspect(prev => ({ ...prev, ...d }));
    setForm(prev => ({ ...prev, ...overrides }));
    return true;
  };

  const advanceStage = async (newStage) => {
    if (newStage === 'lost') { setShowLostPrompt(true); return; }
    setForm(f => ({ ...f, stage: newStage }));
    await save({ stage: newStage });
  };

  const confirmLost = async () => {
    const ok = await save({ stage: 'lost', lost_reason: lostReason });
    if (ok) setShowLostPrompt(false);
  };

  const addActivity = async (e) => {
    e.preventDefault();
    setActError('');
    setActSaving(true);
    const res = await API(`/api/prospects/${id}/activities`, {
      method: 'POST',
      body: JSON.stringify({ type: actType, body: actBody }),
    });
    const d = await res.json();
    setActSaving(false);
    if (!res.ok) { setActError(d.error || 'Failed'); return; }
    setActivities(prev => [...prev, d]);
    setActBody('');
    load();
  };

  const calculatedMrr = calcMrr(form.tier_interest, form.firm_size);

  if (loading) return <div className="dashboard-container"><Sidebar /><main className="dashboard-main"><div style={{ padding:'40px', color:'#6b7280' }}>Loading…</div></main></div>;
  if (error)   return <div className="dashboard-container"><Sidebar /><main className="dashboard-main"><div style={alertRed}>{error}</div></main></div>;

  const curStageIdx = STAGES.indexOf(form.stage);
  const [stageBg, stageCol] = STAGE_COLOURS[form.stage] || ['#f3f4f6','#6b7280'];

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-main">
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'4px' }}>
          <button onClick={() => navigate('/owner/prospects')} style={{ background:'none', border:'none', color:'#6b7280', cursor:'pointer', fontSize:'13px', padding:'0' }}>← Pipeline</button>
          <span style={{ color:'#d1d5db' }}>|</span>
          <h1 style={{ fontSize:'22px', fontWeight:700 }}>{prospect.company_name}</h1>
          <span style={{ background:stageBg, color:stageCol, padding:'3px 12px', borderRadius:'999px', fontSize:'12px', fontWeight:700, textTransform:'capitalize' }}>
            {form.stage.replace(/_/g,' ')}
          </span>
        </div>
        <p style={{ color:'#6b7280', marginBottom: prospect.referrer_name ? '6px' : '24px', fontSize:'13px' }}>
          {prospect.contact_name} · {prospect.contact_email}
          {prospect.last_contacted_at && <> · Last contact: {new Date(prospect.last_contacted_at).toLocaleDateString('en-GB')}</>}
        </p>
        {prospect.referrer_name && (
          <p style={{ color:'#6d28d9', marginBottom:'24px', fontSize:'13px', fontWeight:600 }}>
            ↪ Referred by {prospect.referrer_name}{prospect.referrer_email ? ` · ${prospect.referrer_email}` : ''}
          </p>
        )}

        {saveError && <div style={alertRed}>{saveError}</div>}

        {/* Converted banner */}
        {convertedResult && (
          <div style={{ background:'#d1fae5', border:'1px solid #6ee7b7', borderRadius:'10px', padding:'12px 18px', marginBottom:'18px', display:'flex', alignItems:'center', gap:'14px' }}>
            <span style={{ fontSize:'20px' }}>🎉</span>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700, color:'#065f46', fontSize:'14px' }}>Converted to broker company</div>
              <div style={{ fontSize:'13px', color:'#047857' }}>{convertedResult.company_name}</div>
            </div>
            <button
              onClick={() => navigate('/owner/companies')}
              style={{ background:'#10b981', color:'#fff', border:'none', borderRadius:'8px', padding:'8px 16px', fontWeight:600, fontSize:'13px', cursor:'pointer' }}
            >
              View Companies →
            </button>
          </div>
        )}

        {/* Stage progression */}
        <div style={{ background:'#fff', borderRadius:'12px', border:'1px solid #e5e7eb', padding:'16px 20px', marginBottom:'20px' }}>
          <div style={{ fontSize:'12px', fontWeight:600, color:'#9ca3af', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:'12px' }}>Stage Progression</div>
          <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', alignItems:'center' }}>
            {STAGES.filter(s => s !== 'lost' && s !== 'converted').map((s) => {
              const isCur = s === form.stage;
              const isPast = STAGES.indexOf(s) < curStageIdx && form.stage !== 'lost';
              const [bg, col] = STAGE_COLOURS[s];
              return (
                <button
                  key={s}
                  onClick={() => advanceStage(s)}
                  disabled={isCur || saving}
                  style={{
                    padding:'7px 16px', borderRadius:'999px', fontSize:'12px', fontWeight:700, cursor: isCur ? 'default' : 'pointer',
                    border: isCur ? `2px solid ${col}` : '2px solid transparent',
                    background: isCur ? col : isPast ? bg : '#f3f4f6',
                    color: isCur ? '#fff' : isPast ? col : '#9ca3af',
                    textTransform:'capitalize', transition:'all .15s',
                  }}
                >
                  {isCur && '✓ '}{s.replace(/_/g,' ')}
                </button>
              );
            })}

            {/* Convert button — replaces plain 'converted' stage pill */}
            {convertedResult ? (
              <span style={{ padding:'7px 16px', borderRadius:'999px', fontSize:'12px', fontWeight:700, background:STAGE_COLOURS.converted[0], color:STAGE_COLOURS.converted[1], border:`2px solid ${STAGE_COLOURS.converted[1]}` }}>
                ✓ Converted
              </span>
            ) : (
              <button
                onClick={openConvertModal}
                disabled={saving}
                style={{ padding:'7px 18px', borderRadius:'999px', fontSize:'12px', fontWeight:700, cursor:'pointer', background:'#10b981', color:'#fff', border:'2px solid #10b981', transition:'all .15s' }}
              >
                Convert to Broker →
              </button>
            )}

            <button
              onClick={() => advanceStage('lost')}
              disabled={form.stage === 'lost' || saving}
              style={{
                padding:'7px 16px', borderRadius:'999px', fontSize:'12px', fontWeight:700, cursor: form.stage === 'lost' ? 'default' : 'pointer',
                border: form.stage === 'lost' ? '2px solid #6b7280' : '2px solid transparent',
                background: form.stage === 'lost' ? '#6b7280' : '#fee2e2', color: form.stage === 'lost' ? '#fff' : '#991b1b',
              }}
            >
              {form.stage === 'lost' ? '✓ Lost' : 'Mark Lost'}
            </button>
          </div>
        </div>

        {/* Lost reason prompt */}
        {showLostPrompt && (
          <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) setShowLostPrompt(false); }}>
            <div style={{ background:'#fff', borderRadius:'12px', padding:'28px', maxWidth:'420px', width:'100%' }}>
              <h3 style={{ fontSize:'18px', fontWeight:700, marginBottom:'12px' }}>Mark as Lost</h3>
              <p style={{ fontSize:'13px', color:'#6b7280', marginBottom:'16px' }}>Optionally record why this prospect was lost.</p>
              <textarea
                value={lostReason}
                onChange={e => setLostReason(e.target.value)}
                placeholder="e.g. Went with a competitor, price too high, no response…"
                style={{ width:'100%', padding:'10px', border:'1px solid #d1d5db', borderRadius:'8px', fontSize:'13px', minHeight:'80px', resize:'vertical', fontFamily:'inherit' }}
              />
              {saveError && <div style={{...alertRed, marginTop:'8px'}}>{saveError}</div>}
              <div style={{ display:'flex', gap:'10px', marginTop:'16px' }}>
                <button onClick={confirmLost} disabled={saving} style={{ flex:1, background:'#6b7280', color:'#fff', border:'none', borderRadius:'8px', padding:'10px', fontWeight:600, fontSize:'13px', cursor:'pointer' }}>
                  {saving ? 'Saving…' : 'Confirm Lost'}
                </button>
                <button onClick={() => setShowLostPrompt(false)} style={{ flex:1, background:'#f3f4f6', color:'#374151', border:'none', borderRadius:'8px', padding:'10px', fontWeight:600, fontSize:'13px', cursor:'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Convert to Broker Company modal */}
        {showConvertModal && (
          <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) setShowConvertModal(false); }}>
            <div style={{ background:'#fff', borderRadius:'14px', padding:'28px', maxWidth:'520px', width:'100%', maxHeight:'90vh', overflowY:'auto' }}>
              <h3 style={{ fontSize:'18px', fontWeight:700, marginBottom:'4px' }}>Convert to Broker Company</h3>
              <p style={{ fontSize:'13px', color:'#6b7280', marginBottom:'20px' }}>Creates a new broker company and owner account. The owner will receive a welcome email.</p>
              {convertError && <div style={{...alertRed, marginBottom:'16px'}}>{convertError}</div>}
              <form onSubmit={handleConvert}>
                <div style={{ marginBottom:'14px' }}>
                  <label style={labelStyle}>Company name *</label>
                  <input required value={convertForm.company_name} onChange={e => setConvertForm(f => ({...f, company_name: e.target.value}))} style={inputStyle} />
                </div>
                <div style={grid2}>
                  <div style={{ marginBottom:'14px' }}>
                    <label style={labelStyle}>Owner name *</label>
                    <input required value={convertForm.admin_name} onChange={e => setConvertForm(f => ({...f, admin_name: e.target.value}))} style={inputStyle} />
                  </div>
                  <div style={{ marginBottom:'14px' }}>
                    <label style={labelStyle}>Owner email *</label>
                    <input required type="email" value={convertForm.admin_email} onChange={e => setConvertForm(f => ({...f, admin_email: e.target.value}))} style={inputStyle} />
                  </div>
                  <div style={{ marginBottom:'14px' }}>
                    <label style={labelStyle}>Owner phone</label>
                    <input type="tel" value={convertForm.admin_phone} onChange={e => setConvertForm(f => ({...f, admin_phone: e.target.value}))} style={inputStyle} />
                  </div>
                  <div style={{ marginBottom:'14px' }}>
                    <label style={labelStyle}>Subscription tier</label>
                    <select value={convertForm.subscription_tier} onChange={e => setConvertForm(f => ({...f, subscription_tier: e.target.value}))} style={inputStyle}>
                      <option value="flat">Flat rate (£10/seat/month, or company override)</option>
                    </select>
                  </div>
                  <div style={{ marginBottom:'14px' }}>
                    <label style={labelStyle}>Trial months (0 = active now)</label>
                    <input type="number" min="0" max="12" value={convertForm.trial_months} onChange={e => setConvertForm(f => ({...f, trial_months: e.target.value}))} style={inputStyle} />
                  </div>
                </div>
                <div style={{ marginBottom:'20px' }}>
                  <label style={labelStyle}>Temporary password * <span style={{ color:'#9ca3af', fontWeight:400 }}>(share with owner — they should change it immediately)</span></label>
                  <div style={{ display:'flex', gap:'8px' }}>
                    <input required value={convertForm.admin_password} onChange={e => setConvertForm(f => ({...f, admin_password: e.target.value}))} style={{...inputStyle, fontFamily:'monospace', flex:1}} />
                    <button type="button" onClick={() => setConvertForm(f => ({...f, admin_password: generatePassword()}))} style={{ padding:'8px 12px', background:'#f3f4f6', border:'1px solid #d1d5db', borderRadius:'6px', fontSize:'12px', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap', color:'#374151' }}>
                      Regenerate
                    </button>
                  </div>
                </div>
                <div style={{ display:'flex', gap:'10px' }}>
                  <button type="submit" disabled={convertSaving} style={{ flex:1, background:'#10b981', color:'#fff', border:'none', borderRadius:'8px', padding:'11px', fontWeight:700, fontSize:'14px', cursor:'pointer' }}>
                    {convertSaving ? 'Creating…' : 'Create Broker Company'}
                  </button>
                  <button type="button" onClick={() => setShowConvertModal(false)} style={{ flex:1, background:'#f3f4f6', color:'#374151', border:'none', borderRadius:'8px', padding:'11px', fontWeight:600, fontSize:'14px', cursor:'pointer' }}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div style={{ display:'grid', gridTemplateColumns:'1.4fr 1fr', gap:'20px', alignItems:'start' }}>
          {/* Edit form */}
          <div style={cardStyle}>
            <div style={sectionTitle}>Details</div>
            <div style={grid2}>
              {[
                ['company_name','Company name','text'],
                ['contact_name','Contact name','text'],
                ['contact_email','Email','email'],
                ['contact_phone','Phone','tel'],
              ].map(([field,label,type]) => (
                <div key={field} style={{ marginBottom:'14px' }}>
                  <label style={labelStyle}>{label}</label>
                  <input type={type} value={form[field]} onChange={e => setForm(f => ({...f,[field]:e.target.value}))} style={inputStyle} />
                </div>
              ))}
            </div>
            <div style={grid2}>
              <div style={{ marginBottom:'14px' }}>
                <label style={labelStyle}>Firm size</label>
                <input type="number" min="1" value={form.firm_size} onChange={e => setForm(f => ({...f,firm_size:e.target.value}))} style={inputStyle} />
              </div>
              <div style={{ marginBottom:'14px' }}>
                <label style={labelStyle}>Source</label>
                <select value={form.source} onChange={e => setForm(f => ({...f,source:e.target.value}))} style={inputStyle}>
                  <option value="">Select…</option>
                  {['website','linkedin','referral','introducer_referral','cold_outreach','conference','inbound','other'].map(s => (
                    <option key={s} value={s}>{s.replace(/_/g,' ')}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom:'14px' }}>
                <label style={labelStyle}>Tier interest</label>
                <select value={form.tier_interest} onChange={e => setForm(f => ({...f,tier_interest:e.target.value}))} style={inputStyle}>
                  <option value="">Select…</option>
                  <option value="solo">Solo (£9.99/mo)</option>
                  <option value="small_firm">Small Firm (£10 + £5/user)</option>
                  <option value="firm">Firm (£50 + £4/user)</option>
                </select>
              </div>
              <div style={{ marginBottom:'14px' }}>
                <label style={labelStyle}>Follow-up date</label>
                <input type="date" value={form.follow_up_date} onChange={e => setForm(f => ({...f,follow_up_date:e.target.value}))} style={inputStyle} />
              </div>
              <div style={{ marginBottom:'14px' }}>
                <label style={labelStyle}>Est. MRR override (£)</label>
                <input type="number" step="0.01" value={form.estimated_mrr} onChange={e => setForm(f => ({...f,estimated_mrr:e.target.value}))} style={inputStyle} placeholder={calculatedMrr ? `Auto: £${calculatedMrr.toFixed(2)}` : ''} />
              </div>
              {calculatedMrr !== null && (
                <div style={{ marginBottom:'14px' }}>
                  <label style={labelStyle}>Calculated MRR</label>
                  <div style={{ padding:'8px 12px', background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:'6px', fontSize:'13px', color:'#065f46', fontWeight:600 }}>
                    £{calculatedMrr.toFixed(2)}/mo ({form.tier_interest.replace(/_/g,' ')})
                  </div>
                </div>
              )}
            </div>
            <div style={{ marginBottom:'14px' }}>
              <label style={labelStyle}>Notes</label>
              <textarea value={form.notes} onChange={e => setForm(f => ({...f,notes:e.target.value}))} style={{...inputStyle,minHeight:'80px',resize:'vertical'}} />
            </div>
            {form.stage === 'lost' && (
              <div style={{ marginBottom:'14px' }}>
                <label style={labelStyle}>Lost reason</label>
                <textarea value={lostReason} onChange={e => setLostReason(e.target.value)} style={{...inputStyle,minHeight:'60px',resize:'vertical'}} />
              </div>
            )}
            <button
              onClick={() => save(form.stage === 'lost' ? { lost_reason: lostReason } : {})}
              disabled={saving}
              style={{ background:'#10b981', color:'#fff', border:'none', borderRadius:'8px', padding:'10px 24px', fontWeight:600, fontSize:'14px', cursor:'pointer' }}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>

          {/* Activity log */}
          <div style={cardStyle}>
            <div style={sectionTitle}>Activity Log</div>
            {activities.length === 0 && <div style={{ fontSize:'13px', color:'#9ca3af', marginBottom:'16px' }}>No activities yet.</div>}
            <div style={{ maxHeight:'340px', overflowY:'auto', marginBottom:'20px' }}>
              {activities.map(a => (
                <div key={a.id} style={{ display:'flex', gap:'10px', marginBottom:'14px', paddingBottom:'14px', borderBottom:'1px solid #f3f4f6' }}>
                  <span style={{ fontSize:'18px', flexShrink:0, marginTop:'1px' }}>{ACTIVITY_ICONS[a.type] || '📋'}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'3px' }}>
                      <span style={{ fontSize:'11px', fontWeight:700, textTransform:'capitalize', color:a.type === 'website_enquiry' ? '#1d4ed8' : '#374151' }}>
                        {a.type === 'website_enquiry' ? 'Website Enquiry' : a.type}
                      </span>
                      <span style={{ fontSize:'11px', color:'#9ca3af' }}>·</span>
                      <span style={{ fontSize:'11px', color:'#9ca3af' }}>{new Date(a.created_at).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })}</span>
                      {a.created_by && <><span style={{ fontSize:'11px', color:'#9ca3af' }}>·</span><span style={{ fontSize:'11px', color:'#9ca3af' }}>{a.created_by}</span></>}
                    </div>
                    <div style={{ fontSize:'13px', color:'#374151', lineHeight:'1.5' }}>{a.body}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ borderTop:'1px solid #e5e7eb', paddingTop:'16px' }}>
              <div style={{ fontSize:'12px', fontWeight:600, color:'#374151', marginBottom:'10px' }}>Add Activity</div>
              {actError && <div style={alertRed}>{actError}</div>}
              <form onSubmit={addActivity}>
                <select value={actType} onChange={e => setActType(e.target.value)} style={{...inputStyle, marginBottom:'10px'}}>
                  {['call','email','meeting','demo','note'].map(t => (
                    <option key={t} value={t}>{ACTIVITY_ICONS[t]} {t.charAt(0).toUpperCase()+t.slice(1)}</option>
                  ))}
                </select>
                <textarea required value={actBody} onChange={e => setActBody(e.target.value)} placeholder="Notes about this activity…" style={{...inputStyle, minHeight:'70px', resize:'vertical', marginBottom:'10px'}} />
                <button type="submit" disabled={actSaving} style={{ background:'#0e1b2c', color:'#fff', border:'none', borderRadius:'8px', padding:'8px 18px', fontWeight:600, fontSize:'13px', cursor:'pointer', width:'100%' }}>
                  {actSaving ? 'Adding…' : 'Add Activity'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

const alertRed = { background:'#fee2e2', color:'#991b1b', padding:'10px 14px', borderRadius:'8px', fontSize:'13px', marginBottom:'16px' };
const overlayStyle = { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' };
const cardStyle = { background:'#fff', borderRadius:'12px', border:'1px solid #e5e7eb', padding:'20px' };
const sectionTitle = { fontSize:'13px', fontWeight:700, color:'#374151', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:'16px', paddingBottom:'10px', borderBottom:'1px solid #f3f4f6' };
const grid2 = { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 16px' };
const labelStyle = { display:'block', fontSize:'12px', fontWeight:600, color:'#374151', marginBottom:'4px' };
const inputStyle = { width:'100%', padding:'8px 12px', border:'1px solid #d1d5db', borderRadius:'6px', fontSize:'13px', fontFamily:'inherit', color:'#111827', background:'#fff', boxSizing:'border-box' };
